import React, {
  FocusEvent,
  KeyboardEvent,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import clsx from 'clsx';
import DateTextField from 'core/components/DateTimePicker/inputs/DateTextField';
import {
  notEmptyString,
} from '@populateui/utils';
import dayjs from 'utils/dayjs';
import {
  PickerDateRange,
} from 'core/components/DateTimePicker/types';

type DateRangeInputRef = {
  setFocusedFrom: (focused: boolean) => void;
  setFocusedTo: (focused: boolean) => void;
  focusFromInput: () => void;
  focusToInput: () => void;
  focusedFrom: boolean;
  focusedTo: boolean;
  focusContainer: () => void;
};

type Props = {
  id?: string;
  value?: PickerDateRange;
  onChange: (value: PickerDateRange | undefined) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>, field: 'from' | 'to') => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>, field: 'from' | 'to') => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>, field: 'from' | 'to') => void;
  className?: string;
  disabled?: boolean;
  fromPlaceholder?: string;
  toPlaceholder?: string;
  fromLabel?: string;
  toLabel?: string;
};

const labelClassName = 'font-semibold bg-gray-200 text-xs whitespace-nowrap px-2 py-1 rounded-full text-gray-900';

function getContainerClassName(focused: boolean, otherFocused: boolean) {
  return clsx('flex items-center justify-between gap-2 px-1 p-0.5 border-none rounded-full transition-all w-[190px] focus-visible:!rounded-full', {
    'border-gray-500 bg-white': focused,
    'border-gray-300': !focused,
    'bg-inherit': otherFocused,
    'shadow-[0px_9px_29px_-12px_rgba(0,_0,_0,_0.1)]': focused,
  });
}

type ClearButtonProps = {
  onClick: () => void;
};

function ClearButton({ onClick }: ClearButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center justify-center w-6 h-6 border border-transparent rounded-full hover:bg-gray-100 focus-visible:bg-gray-100 focus-visible:border-gray-300 text-black transition-colors"
      aria-label="Clear date range"
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M9 3L3 9M3 3L9 9"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

const DateRangeInput = forwardRef<DateRangeInputRef, Props>(({
  value,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  className,
  disabled = false,
  fromPlaceholder = 'mm/dd/yyyy',
  toPlaceholder = 'mm/dd/yyyy',
  fromLabel = 'from:',
  toLabel = 'to:',
  ...props
}, ref) => {
  const fromInputRef = useRef<HTMLInputElement>(null);
  const toInputRef = useRef<HTMLInputElement>(null);
  const fromContainerRef = useRef<HTMLDivElement>(null);

  const [focusedFrom, setFocusedFromState] = useState(true);
  const [focusedTo, setFocusedToState] = useState(false);

  useImperativeHandle(ref, () => ({
    setFocusedFrom: (focused: boolean) => setFocusedFromState(focused),
    setFocusedTo: (focused: boolean) => setFocusedToState(focused),
    focusFromInput: () => fromInputRef.current?.focus(),
    focusToInput: () => toInputRef.current?.focus(),
    focusedFrom,
    focusedTo,
    focusContainer: () => {
      fromContainerRef.current?.focus();
    },
  }));

  const parseDate = (date: string | Date | null | undefined): Date | undefined => {
    if (date instanceof Date) {
      return date;
    }
    if (!date || typeof date !== 'string') {
      return undefined;
    }

    // Handle MM/DD/YYYY format from masked input
    const parts = date.split('/');
    if (parts.length === 3) {
      const month = parseInt(parts[0], 10);
      const day = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);

      // Validate the parts
      if (Number.isNaN(month) || Number.isNaN(day) || Number.isNaN(year)) {
        return undefined;
      }

      if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900) {
        return undefined;
      }

      const parsedDate = new Date(year, month - 1, day);

      // Verify the date is valid
      if (
        parsedDate.getMonth() !== month - 1
        || parsedDate.getDate() !== day
        || parsedDate.getFullYear() !== year
      ) {
        return undefined;
      }

      return parsedDate;
    }

    return undefined;
  };

  const handleFromChange = (date: string | Date | null | undefined) => {
    const fromDate = parseDate(date);
    onChange({
      from: fromDate,
      to: value?.to,
    });
  };

  const handleToChange = (date: string | Date | null | undefined) => {
    const toDate = parseDate(date);
    onChange({
      from: value?.from,
      to: dayjs(toDate).endOf('day').toDate(),
    });
  };

  const handleFromFocus = (e: FocusEvent<HTMLInputElement>) => {
    setFocusedFromState(true);
    setFocusedToState(false);
    onFocus?.(e, 'from');
  };

  const handleToFocus = (e: FocusEvent<HTMLInputElement>) => {
    setFocusedToState(true);
    setFocusedFromState(false);
    onFocus?.(e, 'to');
  };

  const handleFromBlur = (e: FocusEvent<HTMLInputElement>) => {
    // Note: Do NOT set focusedFrom to false on blur
    onBlur?.(e, 'from');
  };

  const handleToBlur = (e: FocusEvent<HTMLInputElement>) => {
    // Note: Do NOT set focusedTo to false on blur
    onBlur?.(e, 'to');
  };

  const handleFromKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(e, 'from');
  };

  const handleToKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(e, 'to');
  };

  const handleClearClick = () => {
    onChange(undefined);
    setFocusedFromState(true);
    setFocusedToState(false);
    // Focus the from input after clearing
    setTimeout(() => {
      fromInputRef.current?.focus();
    }, 0);
  };

  const handleFromContainerClick = () => {
    fromInputRef.current?.focus();
  };

  const handleToContainerClick = () => {
    toInputRef.current?.focus();
  };

  const handleFromContainerKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fromInputRef.current?.focus();
    }
  };

  const handleToContainerKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toInputRef.current?.focus();
    }
  };

  return (
    <div className={clsx('flex items-center border rounded-full bg-gray-200', className, {
      'bg-gray-200': focusedFrom || focusedTo,
    })}
    >
      <div
        className={getContainerClassName(focusedFrom, focusedTo)}
        onClick={handleFromContainerClick}
        onKeyDown={handleFromContainerKeyDown}
        role="button"
        tabIndex={-1}
        aria-label="Click to focus from date input"
        ref={fromContainerRef}
      >
        <div className="flex items-center gap-2">
          <span className={labelClassName}>
            {fromLabel}
          </span>
          <DateTextField
            {...props}
            value={value?.from ?? ''}
            onChange={handleFromChange}
            onFocus={handleFromFocus}
            onBlur={handleFromBlur}
            onKeyDown={handleFromKeyDown}
            inputRef={fromInputRef}
            disabled={disabled}
            placeholder={fromPlaceholder}
            className="border-0 bg-transparent p-0 focus:ring-0 min-w-0 !w-20 compact-date-input"
          />
        </div>
        {focusedFrom && notEmptyString(value?.from) && (
          <ClearButton onClick={handleClearClick} />
        )}
      </div>
      <div
        className={getContainerClassName(focusedTo, focusedFrom)}
        onClick={handleToContainerClick}
        onKeyDown={handleToContainerKeyDown}
        role="button"
        tabIndex={-1}
        aria-label="Click to focus to date input"
      >
        <div className="flex items-center gap-2">
          <span className={labelClassName}>
            {toLabel}
          </span>
          <DateTextField
            {...props}
            value={value?.to ?? ''}
            onChange={handleToChange}
            onFocus={handleToFocus}
            onBlur={handleToBlur}
            onKeyDown={handleToKeyDown}
            inputRef={toInputRef}
            disabled={disabled}
            placeholder={toPlaceholder}
            className="border-0 bg-transparent p-0 focus:ring-0 min-w-0 !w-20 compact-date-input"
          />
        </div>
        {focusedTo && notEmptyString(value?.to) && (
          <ClearButton onClick={handleClearClick} />
        )}
      </div>
    </div>
  );
});

DateRangeInput.displayName = 'DateRangeInput';

export default DateRangeInput;
export type { DateRangeInputRef, PickerDateRange };
